// Putting a lesson night on Meal Hat's own schedule.
//
// Matt: "have them assigned to days in the regular meal hat rotation.
// Although they don't go into the hat, they just get assigned." That is
// exactly Meal Hat's ONE-OFF shape (store/index.js scheduleMeal): a
// drawnMeals row with a name and `manual: true` and NO mealId, so nothing is
// added to the hat, nothing is drawn later, and the row brings no
// ingredients (this app puts them on the list itself). Meal Hat's schedule,
// its calendar and the Magic Mirror feed all render the row by its name.
//
// `lesson` on the row is this app's own marker so the row can be found and
// moved or removed when the plan changes. Meal Hat ignores it.
//
// Two things Meal Hat does on scheduleMeal that this cannot: regenerate the
// shopping list (a one-off brings nothing, so nothing is lost) and republish
// the Magic Mirror feed (it republishes on its next open or draw). A draw in
// Meal Hat does not avoid an assigned night — pick the draw's date range
// around the lessons.
import { dbGet, dbPut, dbPatch, dbDelete } from '../store/db.js';
import { LESSONS } from '../curriculum/lessons.js';

const uuid = () => (globalThis.crypto?.randomUUID ? globalThis.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

// Just the meal, short. It used to be "Lesson 1: Pork chops with a mustard
// pan sauce", which Meal Hat's schedule, calendar and the mirror all had to
// squeeze in (Matt, 2026-09-28: "It doesn't need to say lesson one and all
// that stuff").
export const SHORT_NAME_MAX = 22;
export const scheduleName = (lesson) => (lesson.short || lesson.dish).slice(0, SHORT_NAME_MAX);

/**
 * Rows this app already wrote keep whatever name they were written with, so
 * a name change here reaches them only through this: a patch renaming every
 * lesson row whose name is not what `scheduleName` says now. Only rows that
 * exist and carry our `lesson` marker — patching a row id alone would create
 * a stub row if Meal Hat had deleted it.
 */
export function renamePatch (drawnMeals = {}, lessons = []) {
  const patch = {};
  for (const [id, row] of Object.entries(drawnMeals || {})) {
    const lesson = row?.lesson && lessons.find((l) => l.id === row.lesson);
    if (!lesson) continue;
    const name = scheduleName(lesson);
    if (row.name !== name) patch[`drawnMeals/${id}/name`] = name;
  }
  return patch;
}

/** Writes the one-off row; returns its id so the course entry can remember it. */
export async function placeOnSchedule (hat, lesson, isoDate) {
  const id = `${Date.now()}-${uuid()}`;
  await dbPut(`${hat}/drawnMeals/${id}`, { id, name: scheduleName(lesson), assignedDate: isoDate, manual: true, lesson: lesson.id });
  return id;
}

export async function moveOnSchedule (hat, lesson, rowId, isoDate) {
  if (!rowId) return placeOnSchedule(hat, lesson, isoDate);
  await dbPut(`${hat}/drawnMeals/${rowId}`, { id: rowId, name: scheduleName(lesson), assignedDate: isoDate, manual: true, lesson: lesson.id });
  return rowId;
}

export async function removeFromSchedule (hat, rowId) {
  if (!rowId) return;
  await dbDelete(`${hat}/drawnMeals/${rowId}`);
}

/** Brings the names of lesson rows from a fortnight back onward up to date. Quiet: a failure changes nothing. */
export async function refreshScheduleNames (hat) {
  if (!hat) return;
  const from = new Date(Date.now() - 14 * 86400000);
  const iso = `${from.getFullYear()}-${String(from.getMonth() + 1).padStart(2, '0')}-${String(from.getDate()).padStart(2, '0')}`;
  try {
    const drawn = await dbGet(`${hat}/drawnMeals`, `orderBy="assignedDate"&startAt="${iso}"`);
    const patch = renamePatch(drawn, LESSONS);
    if (Object.keys(patch).length) await dbPatch(hat, patch);
  } catch {
    // Names are cosmetic; the next load tries again.
  }
}
